import { formatPrice } from '@shared/lib/format-price';

/** Mirrors `SubscriptionDto` on the backend. */
export interface Subscription {
  id: string;
  status: string;
  planName: string | null;
  planInterval: string | null;
  price: number | null;
  currency: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelledAt: string | null;
  stripeSubscriptionId: string | null;
}

/** Mirrors `UserDto` on the backend. */
export interface PlatformUser {
  id: string;
  email: string | null;
  emailVerified: boolean | null;
  /** Auth display name, falling back to the profile name. Null when unset. */
  displayName: string | null;
  fullName: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  organization: string | null;
  country: string | null;
  countryCode: string | null;
  createdAt: string | null;
  lastSignInAt: string | null;
  /** Budgets owned by this user, excluding soft-deleted ones. */
  budgetCount: number;
  subscription: Subscription | null;
}

export interface ListUsersParams {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  plan?: string;
}

export interface SubscriptionPlanOption {
  id: string;
  name: string;
  interval: string | null;
  price: number | null;
  currency: string | null;
  /** False for a retired plan. Still assignable, but marked as such. */
  isActive: boolean;
}

/** The statuses an admin may assign, mirroring the backend's enum. */
export const ASSIGNABLE_STATUSES = ['active', 'trial', 'cancelled', 'expired'] as const;

export type AssignableStatus = (typeof ASSIGNABLE_STATUSES)[number];

/**
 * Demo access is granted as a trial, so it is distinguishable from a real
 * subscription everywhere in the admin — and it is the status the expiry sweep
 * looks for.
 */
export const DEMO_STATUS = 'trial' as const satisfies AssignableStatus;

/** Demo lengths the dialog offers, in days. */
export const DEMO_DURATIONS = [7, 14, 30] as const;

export const DEFAULT_DEMO_DAYS = 14;

/** `YYYY-MM-DD` for a date input, `days` from today. */
export function demoEndDate(days: number, from: Date = new Date()): string {
  const end = new Date(from);
  end.setDate(end.getDate() + days);

  return end.toISOString().slice(0, 10);
}

/** Body of `POST /budgeting/users/:userId/subscriptions`. */
export interface AssignSubscriptionInput {
  planId: string;
  status: AssignableStatus;
  /** `YYYY-MM-DD`. Left out, the server starts the period now. */
  startsAt?: string;
  /** `YYYY-MM-DD`. Left out, the server derives it from the plan's interval. */
  endsAt?: string;
  trialEndsAt?: string;
}

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface UsersPage {
  users: PlatformUser[];
  meta: PageMeta;
}

/** Filter value for users who have never had a subscription. */
export const NO_SUBSCRIPTION = 'none';

/**
 * Groups the free-text `subscriptions.status` into the three states worth
 * distinguishing visually. Anything unrecognised falls back to neutral rather
 * than being shown as a problem.
 */
export type StatusTone = 'positive' | 'warning' | 'neutral';

const POSITIVE = new Set(['active', 'trialing', 'trial']);
const WARNING = new Set([
  'past_due',
  'unpaid',
  'incomplete',
  'incomplete_expired',
  'canceled',
  'cancelled',
  'expired',
  'paused',
]);

export function statusTone(status: string | undefined): StatusTone {
  if (!status) {
    return 'neutral';
  }

  const normalized = status.toLowerCase();

  if (POSITIVE.has(normalized)) {
    return 'positive';
  }

  return WARNING.has(normalized) ? 'warning' : 'neutral';
}

/** `past_due` → `Past due` */
export function humanizeStatus(status: string): string {
  const spaced = status.replace(/[_-]+/g, ' ').trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * The billing cadence, phrased for a person: "month" → "Billed monthly",
 * "lifetime" → "One-time".
 *
 * Returns null when the interval adds nothing — a plan literally named
 * "Monthly" billed monthly should not read "Monthly · monthly".
 */
export function describeInterval(subscription: Subscription): string | null {
  const interval = subscription.planInterval?.trim().toLowerCase();

  if (!interval) {
    return null;
  }

  const planName = subscription.planName?.trim().toLowerCase() ?? '';

  const phrasing: Record<string, string> = {
    month: 'Billed monthly',
    monthly: 'Billed monthly',
    year: 'Billed yearly',
    yearly: 'Billed yearly',
    week: 'Billed weekly',
    day: 'Billed daily',
    lifetime: 'One-time payment',
    once: 'One-time payment',
  };

  const phrase = phrasing[interval];

  if (!phrase) {
    // Unknown cadence: show it plainly rather than inventing an adverb.
    return `Billed ${interval}`;
  }

  // "Monthly" billed monthly, "Yearly" billed yearly — the name already says it.
  if (planName === interval || planName === `${interval}ly` || planName.includes(interval)) {
    return null;
  }

  return phrase;
}

/**
 * A display name, or null when the record only has an email. Resolved on the
 * server — the auth identity's display name wins over the profile name.
 */
export function displayName(user: PlatformUser): string | null {
  return user.displayName?.trim() || null;
}

/** "Selenia Garcia" → "SG"; falls back to the email's first letter. */
export function initialsOf(user: PlatformUser): string {
  const name = displayName(user);

  if (name) {
    const words = name.split(/\s+/);
    const first = words[0]?.[0] ?? '';
    const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';

    return (first + last).toUpperCase();
  }

  return (user.email?.[0] ?? '?').toUpperCase();
}

/**
 * How a plan reads in a picker: "Yearly · 149$ · Billed yearly". Two plans share
 * the name "Monthly", so the price and cadence are what tell them apart.
 */
export function describePlanOption(plan: SubscriptionPlanOption): string {
  const parts = [plan.name];
  const price = formatPrice(plan.price, plan.currency);

  if (price) {
    parts.push(price);
  }

  const interval = plan.interval?.trim().toLowerCase();

  // Only worth saying when the name does not already carry it.
  if (interval && !plan.name.toLowerCase().includes(interval)) {
    parts.push(interval === 'lifetime' ? 'one-time' : `per ${interval}`);
  }

  return parts.join(' · ');
}
